import React, { FC, useState, useEffect } from "react";
import { createUseStyles } from "react-jss";
import { useSelector, useDispatch } from "react-redux";

import MediaViewer from "Components/MediaViewer";
import MediaViewerSmallViewPort from "Components/MediaViewerSmallViewPort";

import { useQuery } from "@apollo/client";

import Photoviewer from "Components/Photoviewer";
import Videoviewer from "Components/Videoviewer";
import Panoviewer from "Components/Panoviewer";
import InstagramViewer from "Components/InstagramViewer";
import { Loading } from "Components/Loading";

import { resetAudio } from "Containers/AudioPlayer/AudioPlayerActions";
import {
  setSelectedMedia,
  setSelectedIndex,
  setDisplay,
  setPanoFullScreen,
  setShowMediaObj,
  setMediaParam,
} from "Containers/MediaViewer";

import PhotoViewerSmallViewPort from "Components/PhotoviewerSmallViewPort";
import VideoViewerSmallViewPort from "Components/VideoViewerSmallViewPort";
import PanoViewerSmallViewPort from "Components/PanoViewerSmallViewPort";
import InstagramViewerSmallViewPort from "Components/InstagramViewerSmallViewPort";

import sortMediaPriority from "Utils/sortMediaPriority";

import stopGQL from "./gqlQuery";
import styles from "./styles";

interface Props {
  stop: any;
  mediaViewer: string;
  smallViewPort: boolean;
  locId: any;
  setShowMediaViewer: (val: any) => void;
  setSceneOpen: () => void;
}

const renderViewer = (
  mediaType: string,
  media: any,
  selectedIdx: number,
  changePanoFullScreen: any
) => {
  if (media[selectedIdx].network === "instagram")
    return <InstagramViewer media={media} selectedIdx={selectedIdx} />;
  switch (mediaType) {
    case "photo":
      return <Photoviewer media={media} selectedIdx={selectedIdx} />;
    case "video":
      return <Videoviewer media={media} selectedIdx={selectedIdx} />;
    case "panorama":
      return (
        <Panoviewer
          media={media}
          selectedIdx={selectedIdx}
          changePanoFullScreen={changePanoFullScreen}
        />
      );
    default:
      return <Photoviewer media={media} selectedIdx={selectedIdx} />;
  }
};

const MediaViewerContainer: FC<Props> = (props: Props) => {
  const {
    stop,
    mediaViewer,
    setShowMediaViewer,
    smallViewPort,
    locId,
    setSceneOpen,
  } = props;
  const useStyles = createUseStyles(styles);
  const classes = useStyles();

  const dispatch = useDispatch();

  const { audio } = useSelector((state: any) => state.audioPlayer);
  const {
    selectedMedia,
    selectedIndex,
    display,
    fullScreen,
    showMediaObj,
    mediaParam,
  } = useSelector((state: any) => state.mediaViewer);

  const [errorIdx, setErrorIdx] = useState([]);

  const setAudio = () => dispatch(resetAudio());

  const handleMediaClick = (idx: number) => {
    dispatch(setSelectedIndex(idx));
    dispatch(setShowMediaObj(true));
    dispatch(setDisplay(true));
    resetAudio();
  };

  const handleCloseMediaViewer = () => {
    dispatch(setShowMediaViewer(false));
    dispatch(setSelectedIndex(0));
    dispatch(setMediaParam(null));
  };

  const renderViewerSmall = (
    mediaType: string,
    // eslint-disable-next-line no-shadow
    media: any,
    // eslint-disable-next-line no-shadow
    selectedIdx: number
  ) => {
    if (media[selectedIdx].network === "instagram")
      return (
        <InstagramViewerSmallViewPort
          media={media}
          selectedIdx={selectedIdx}
          changeDisplay={(val: any) => dispatch(setDisplay(val))}
          handleMediaClick={handleMediaClick}
        />
      );
    switch (mediaType) {
      case "photo":
        return (
          <PhotoViewerSmallViewPort
            media={media}
            selectedIdx={selectedIdx}
            changeDisplay={(val: any) => dispatch(setDisplay(val))}
            handleMediaClick={handleMediaClick}
          />
        );
      case "video":
        return (
          <VideoViewerSmallViewPort
            media={media}
            selectedIdx={selectedIdx}
            changeDisplay={(val: any) => dispatch(setDisplay(val))}
            handleMediaClick={handleMediaClick}
          />
        );
      case "panorama":
        return (
          <PanoViewerSmallViewPort
            media={media}
            selectedIdx={selectedIdx}
            changeDisplay={(val: any) => dispatch(setDisplay(val))}
            handleMediaClick={handleMediaClick}
          />
        );
      default:
        return (
          <PhotoViewerSmallViewPort
            media={media}
            selectedIdx={selectedIdx}
            changeDisplay={(val: any) => dispatch(setDisplay(val))}
            handleMediaClick={handleMediaClick}
          />
        );
    }
  };

  const { data } = useQuery(stopGQL, {
    variables: {
      loc_id: locId,
    },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (errorIdx.length > 0)
      dispatch(
        setSelectedMedia((m) =>
          m.filter((elem: any) => !errorIdx.includes(elem.id))
        )
      );
  }, [errorIdx, dispatch]);

  useEffect(() => {
    if (data) {
      const chosenStop = data.locations.stops.filter(
        (stopObj: any) => stopObj.stopid === stop.id
      );

      let panos = [];
      let videos = [];
      let photos = [];

      if (chosenStop[0]) {
        if (mediaParam) {
          const mediaListSorted = sortMediaPriority(
            chosenStop[0][`${mediaViewer}s`]
          );

          const chosenMediaIndex = mediaListSorted.findIndex(
            (media: any) => media.id === parseInt(mediaParam, 10)
          );
          if (chosenMediaIndex !== -1) {
            dispatch(setSelectedIndex(chosenMediaIndex));
          }
        }

        panos = chosenStop[0].panoramas
          ? sortMediaPriority(chosenStop[0].panoramas)
          : [];
        videos = chosenStop[0].videos
          ? sortMediaPriority(chosenStop[0].videos)
          : [];
        photos = chosenStop[0].photos
          ? sortMediaPriority(chosenStop[0].photos)
          : [];
        panos = panos.map((pano: any) => {
          return { ...pano, fullScreen: false, play: true };
        });
      }

      if (mediaViewer === "photo")
        dispatch(
          setSelectedMedia(
            chosenStop[0] ? [...photos, ...panos, ...videos] : null
          )
        );

      if (mediaViewer === "video")
        dispatch(
          setSelectedMedia(
            chosenStop[0] ? [...videos, ...panos, ...photos] : null
          )
        );

      if (mediaViewer === "panorama")
        dispatch(
          setSelectedMedia(
            chosenStop[0] ? [...panos, ...videos, ...photos] : null
          )
        );
    }
  }, [data, stop.id, mediaViewer, smallViewPort, mediaParam, dispatch]);

  if (selectedMedia) {
    return (
      <>
        <div className={classes.desktop}>
          <MediaViewer
            media={selectedMedia}
            showMediaViewer={mediaViewer}
            setShowMediaViewer={setShowMediaViewer}
            selectedIdx={selectedIndex}
            setIdx={setSelectedIndex}
            fullScreen={fullScreen}
            errorIdx={errorIdx}
            setErrorIdx={setErrorIdx}
            handleCloseMediaViewer={handleCloseMediaViewer}
          >
            {selectedMedia[selectedIndex] &&
              renderViewer(
                selectedMedia[selectedIndex]?.mediaType,
                selectedMedia,
                selectedIndex,
                setPanoFullScreen
              )}
          </MediaViewer>
        </div>
        <div className={classes.mobile}>
          <MediaViewerSmallViewPort
            media={selectedMedia}
            setShowMediaViewer={setShowMediaViewer}
            audio={audio}
            stop={stop}
            resetAudio={setAudio}
            handleMediaClick={handleMediaClick}
            mediaObj={showMediaObj}
            errorIdx={errorIdx}
            setErrorIdx={setErrorIdx}
            setSceneOpen={setSceneOpen}
            smallViewPort={smallViewPort}
          >
            {display &&
              selectedMedia[selectedIndex] &&
              renderViewerSmall(
                selectedMedia[selectedIndex]?.mediaType,
                selectedMedia,
                selectedIndex
              )}
          </MediaViewerSmallViewPort>
        </div>
      </>
    );
  }
  return (
    <div
      style={{
        position: "fixed",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        backgroundColor: "rgba(255,255,255,0.5)",
      }}
    >
      <Loading />
    </div>
  );
};

export default MediaViewerContainer;
